import type { DetailedPlace } from '@modules-rocco/location/type/detailed-place';

/**
 * Everything a Get Matched brief can carry, across both variants.
 *
 * One shape rather than one per variant: a step that does not apply to a
 * variant simply is not in its step list, and the unused field stays empty.
 * That keeps the submit payload builder from having to know which flow it came
 * from.
 */
export interface MatchAnswers {
    /** `professional` or `product`. Drives which step list runs. */
    enquiryType: 'professional' | 'product';
    /** Who the reader is, e.g. `Homeowner`. */
    userType: string;
    /** Human-readable location, as shown back to the reader. */
    region: string;
    /**
     * The structured place behind `region`, when one was chosen from the
     * suggestions. Carries suburb, city, region, postcode and coordinates,
     * which is what a project record wants — a display string alone would have
     * to be re-parsed to be useful.
     */
    location: DetailedPlace | null;
    /** Broad shape of the work, e.g. `New Build`. */
    projectType: string;
    /** BuildHub scope slug, e.g. `new-home-build`. */
    projectScope: string;
    /** Professional types wanted, e.g. `Architect`. */
    professionalTypes: string[];
    /** Style tiles chosen, e.g. `Minimalist`. */
    styles: string[];
    /** What the reader is optimising for, e.g. `Budget fit`. */
    priorities: string[];
    /** Budget bucket label. */
    budget: string;
    /** When the work starts, e.g. `Within 1-3 months`. */
    startTiming: string;
    /** How soon they want to hear back, e.g. `Right away`. */
    contactUrgency: string;
    description: string;
    attachments: File[];
    /** Set when the brief is tied to an existing project. */
    buildHubID: number | null;
    /**
     * What the reader said at the opening step, or null until they say.
     *
     * Distinct from `useProjectDetails`, which starts false and so cannot tell
     * "not answered yet" from "answered, and it is not about a project" — an
     * ambiguity that showed up as the second option arriving pre-selected.
     */
    projectChoice: 'existing' | 'new' | null;
    /**
     * Whether to carry the chosen project's details rather than ask for them.
     *
     * True the moment a project is picked, and false again if the reader says
     * they would rather answer themselves. The link survives that: they are
     * still briefing against the project, just not describing it the same way.
     */
    useProjectDetails: boolean;
}

export const createInitialAnswers = (
    enquiryType: MatchAnswers['enquiryType'],
): MatchAnswers => ({
    enquiryType,
    userType: '',
    region: '',
    location: null,
    projectType: '',
    projectScope: '',
    professionalTypes: [],
    styles: [],
    priorities: [],
    budget: '',
    startTiming: '',
    contactUrgency: '',
    description: '',
    attachments: [],
    buildHubID: null,
    projectChoice: null,
    useProjectDetails: false,
});
